import React from "react";
import { Spin, Button } from "antd";
import { useParams } from "react-router-dom";
import { pay, payWithExtendedTimeout, getCartransfer } from "../../../api/cartransferService";
import * as cartransferService from "../../../api/cartransferService";
import CartransferPayF23Form from "./CartransferPayF23Form";
import CartransferPayF23Continue from "./CartransferPayF23Continue";
import { toast } from "../../../utils/toasts";
import { handleError, getErrorMessage } from "../../../utils/errorHandler";
import ErrorNotification from "../../../components/ErrorNotification";
import ProcessingLoader from "../../../components/ProcessingLoader";

const CartransferPayF23 = () => {
  const { cartransferId } = useParams();
  const [cartransfer, setCartransfer] = React.useState();
  const [loading, setLoading] = React.useState({ 
    movements: false, 
    form: false,
    captcha: false,
    processing: false
  });
  const [links, setLinks] = React.useState({});
  const [errorNotification, setErrorNotification] = React.useState({
    visible: false,
    title: "",
    message: "",
    onRetry: null
  });
  const [processingStage, setProcessingStage] = React.useState('captcha');
  const [showRedirectFallback, setShowRedirectFallback] = React.useState(false);

  React.useEffect(() => {
    const fetchCartransfer = async () => {
      const response = await getCartransfer(cartransferId);
      setCartransfer(response.data);
    };
    fetchCartransfer();
  }, [cartransferId]);

  const newF23 = async () => {
    try {
      // Limpiar solo paymentUrl para permitir un nuevo pago F23
      const newCartransfer = await cartransferService.setCartransfer(
        cartransfer.id,
        { paymentUrl: null }
      );
      setCartransfer(newCartransfer.data);
    } catch (error) {
      handleError(error, toast, "Error al crear nuevo F23", "newF23");
    }
  };

  const confirmPayment = async () => {
    toast("info", "Esta acción podría tardar varios momentos.");
    setLoading({ movements: true });
    try {
      const response = await pay(
        cartransfer.id
      );
      const { folder_movements, folder_f23_payment } = response.data;
      setLinks({ folder_movements, folder_f23_payment });
    } catch (error) {
      handleError(error, toast, "Error al confirmar pago", "confirmPayment");
    }
    setLoading({ movements: false });
  };

  const onFinish = async (values) => {
    setLoading({ 
      form: true, 
      captcha: true, 
      processing: true 
    });
    setErrorNotification({ visible: false });
    setProcessingStage('captcha');
    
    try {
      // Simular progreso del captcha
      setTimeout(() => {
        setProcessingStage('processing');
      }, 2000);
      
      // Transformar los datos del notario para que el backend los entienda
      const transformedValues = {
        ...values,
        notary: {
          ...values.notary,
          // Asegurar que fullRut esté presente para el backend
          fullRut: values.notary?.fullRut || `${values.notary?.rut || '13756524'}-${values.notary?.dv || '2'}`
        }
      };

      const response = await payWithExtendedTimeout({
        id: cartransferId,
        ...transformedValues,
      });
      
      if (response.data.paymentUrl) {
        // Actualizar loading para mostrar que se está redirigiendo
        setProcessingStage('redirecting');
        setLoading({ 
          form: false, 
          captcha: false, 
          processing: true 
        });
        
        toast("success", "Formulario procesado exitosamente. Redirigiendo al pago...");
        
        // Guardar la URL en el estado para flujo de continuidad (por si el navegador bloquea redirección)
        setCartransfer((prev) => ({ ...(prev || {}), paymentUrl: response.data.paymentUrl }));

        // Abrir en una nueva pestaña inmediatamente (menos probable que sea bloqueado)
        try {
          window.open(response.data.paymentUrl, '_blank', 'noopener,noreferrer');
        } catch (_) {}

        // Fallback: redirigir la pestaña actual tras un pequeño delay
        setTimeout(() => {
          try { window.location.href = response.data.paymentUrl; } catch (_) {}
        }, 1200);

        // Mostrar botón de respaldo si en 2 minutos no ocurrió la redirección
        setShowRedirectFallback(false);
        setTimeout(() => {
          setShowRedirectFallback(true);
        }, 120000);
      } else {
        setLoading({ 
          form: false, 
          captcha: false, 
          processing: false 
        });
        toast("error", "No se pudo obtener la URL de pago");
      }
    } catch (error) {
      console.error("Error en onFinish:", error);
      
      setLoading({ 
        form: false, 
        captcha: false, 
        processing: false 
      });
      
      // Manejo específico para errores de timeout
      let errorTitle = "Error al enviar formulario F23";
      let errorMessage = getErrorMessage(error, "Error al procesar el formulario F23");
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorTitle = "Timeout del proceso";
        errorMessage = "El proceso de resolución del captcha ha excedido el tiempo límite. Esto puede ocurrir cuando el sistema de Tesorería está sobrecargado. Por favor, intenta nuevamente.";
      } else if (error.response?.status === 500) {
        errorTitle = "Error del servidor";
        errorMessage = "Error interno del servidor durante el procesamiento. Por favor, intenta nuevamente en unos minutos.";
      }
      
      // Mostrar notificación de error estilizada
      setErrorNotification({
        visible: true,
        title: errorTitle,
        message: errorMessage,
        onRetry: () => onFinish(values)
      });
      
      // También mostrar toast tradicional como respaldo
      toast("error", errorMessage);
    }
  };
  if (!cartransfer) {
    return <Spin />;
  }

  if (cartransfer.paymentUrl) {
    return (
      <CartransferPayF23Continue
        cartransfer={cartransfer}
        newF23={newF23}
        loading={loading}
        confirmPayment={confirmPayment}
        links={links}
      />
    );
  }

  return (
    <>
      <CartransferPayF23Form
        cartransfer={cartransfer}
        loading={loading}
        onFinish={onFinish}
      />
      <ProcessingLoader
        visible={loading.processing}
        stage={processingStage}
        message={
          processingStage === 'captcha' 
            ? 'Resolviendo captcha del sistema de Tesorería. Este proceso puede tomar entre 2-5 minutos dependiendo de la carga del servidor. Por favor, mantén esta ventana abierta...'
            : processingStage === 'processing'
            ? 'Procesando y enviando el formulario F23 al sistema de Tesorería...'
            : 'Redirigiendo a la página de pago de Tesorería...'
        }
      />
      {processingStage === 'redirecting' && showRedirectFallback && cartransfer?.paymentUrl && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            href={cartransfer.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Continuar al pago (respaldo)
          </Button>
        </div>
      )}
      <ErrorNotification
        visible={errorNotification.visible}
        title={errorNotification.title}
        message={errorNotification.message}
        onRetry={errorNotification.onRetry}
        onClose={() => setErrorNotification({ visible: false })}
      />
    </>
  );
};

export default CartransferPayF23;
